import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain } from '@feathr/blackbox';
import { Button, ButtonValid, Input, Modal } from '@feathr/components';

import { EDomainSetupStep, useDomainSetupModal } from './DomainSetupModal.hooks';

interface IStepContent {
  body?: ReactNode;
  description: string;
  title: string;
}

export interface IDomainSetupModalProps {
  domain: Domain;
  onClose: () => void;
  opened: boolean;
}

function DomainSetupModal({
  domain,
  onClose,
  opened,
}: Readonly<IDomainSetupModalProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    contentDomainError,
    contentSubdomain,
    emailSubdomain,
    handleAuthenticate,
    handleBack,
    handleCancel,
    handleClose,
    handleContentSubdomainChange,
    handleDomainChange,
    handleEmailSubdomainChange,
    handleNext,
    isCheckingDomain,
    isEmailSubdomainLocked,
    isSubmitting,
    nextErrors,
    step,
  } = useDomainSetupModal({ domain, onClose });

  const suffix = `.${domain.get('email_domain')}`;

  const steps: Record<EDomainSetupStep, IStepContent> = {
    [EDomainSetupStep.Domain]: {
      body: (
        <Input
          disabled={isCheckingDomain}
          isClearable={true}
          key={'domain'}
          label={t('Domain')}
          onChange={handleDomainChange}
          placeholder={t('ie: yourdomain.com')}
          required={true}
          type={'text'}
          value={domain.get('email_domain')}
        />
      ),
      description: t('Add a new domain to use with Feathr.'),
      title: t('Add domain'),
    },
    [EDomainSetupStep.ContentSubdomain]: {
      body: (
        <Input
          helpText={t(
            'We have automatically created a content serving subdomain for you. Only make changes if you are sure they are needed.',
          )}
          key={'content'}
          label={t('Subdomain')}
          onChange={handleContentSubdomainChange}
          required={true}
          suffix={suffix}
          type={'text'}
          validationError={contentDomainError}
          value={contentSubdomain}
        />
      ),
      description: t('Set up your subdomain for serving content you create in Feathr.'),
      title: t('Add content serving subdomain'),
    },
    [EDomainSetupStep.EmailSubdomain]: {
      body: (
        <Input
          disabled={isEmailSubdomainLocked}
          helpText={
            isEmailSubdomainLocked
              ? t(
                  'Email sending settings for this domain have already been configured, and cannot be modified.',
                )
              : t(
                  'We have automatically created an email sending subdomain for you. Only make changes if you are sure they are needed.',
                )
          }
          key={'email'}
          label={t('Subdomain')}
          onChange={handleEmailSubdomainChange}
          required={true}
          suffix={suffix}
          type={'text'}
          value={emailSubdomain}
        />
      ),
      description: t('Set up your subdomain for sending emails from Feathr.'),
      title: t('Add email sending subdomain'),
    },
    [EDomainSetupStep.Authenticate]: {
      description: t(
        'Authenticate your domain so your content serves correctly and your emails reach inboxes instead of spam folders.',
      ),
      title: t('Authenticate domain'),
    },
  };
  const { body, description, title } = steps[step];

  const leftActions =
    step > EDomainSetupStep.Domain ? (
      <Button disabled={isSubmitting} onClick={handleBack}>
        {t('Back')}
      </Button>
    ) : undefined;

  const rightActions =
    step === EDomainSetupStep.Authenticate ? (
      <>
        <Button disabled={isSubmitting} onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button isLoading={isSubmitting} onClick={handleAuthenticate} type={'primary'}>
          {t('Authenticate domain')}
        </Button>
      </>
    ) : (
      <>
        <Button disabled={isCheckingDomain} onClick={handleClose}>
          {t('Cancel')}
        </Button>
        <ButtonValid
          errors={nextErrors}
          isLoading={isCheckingDomain}
          onClick={handleNext}
          type={'primary'}
        >
          {t('Next')}
        </ButtonValid>
      </>
    );

  return (
    <Modal
      description={description}
      leftActions={leftActions}
      onClose={handleClose}
      opened={opened}
      rightActions={rightActions}
      title={title}
    >
      {body}
    </Modal>
  );
}

export default observer(DomainSetupModal);
